import { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { Badge } from "@/components/ui/badge";
import { Mail, CheckCircle } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function EmailLogsPage() {
  useDocumentTitle("Email Logs", "Postmark email history and delivery status.");
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await api.get("/analytics/email-logs");
      setLogs(data.logs); setTotal(data.total);
    } catch (err) {
      console.error("Failed to load email logs:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-4" data-testid="email-logs-page">
      <div className="flex items-center gap-2">
        <Mail size={18} className="text-muted-foreground" />
        <p className="text-xs text-muted-foreground">{total} email log{total !== 1 ? "s" : ""}</p>
      </div>

      <div className="border border-border rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="email-logs-table">
          <thead><tr className="border-b border-border text-left">
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Recipient</th>
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Subject</th>
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs hidden md:table-cell">Type</th>
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Status</th>
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs hidden lg:table-cell">Sent At</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center">
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Mail size={32} className="opacity-30" />
                  <p className="text-sm">No emails sent yet</p>
                  <p className="text-xs">Certificate expiry reminders will appear here</p>
                </div>
              </td></tr>
            ) : logs.map((log, i) => (
              <tr key={log.id} className="border-b border-border hover:bg-slate-50/50 table-row-animate" style={{ animationDelay: `${i * 30}ms` }}>
                <td className="px-4 py-2.5">
                  <p className="font-medium">{log.recipient_name}</p>
                  <p className="text-xs text-muted-foreground">{log.recipient_email}</p>
                </td>
                <td className="px-4 py-2.5 max-w-xs truncate">{log.subject}</td>
                <td className="px-4 py-2.5 hidden md:table-cell">
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-[10px] capitalize">{log.type?.replace("_", " ")}</Badge>
                    {log.automated && <Badge variant="outline" className="text-[9px] bg-sky-100 text-sky-700 border-sky-200">Auto</Badge>}
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <Badge variant="outline" className={`text-[10px] ${log.status === "sent" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                    {log.status === "sent" && <CheckCircle size={10} className="mr-1" />}
                    {log.status}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 text-muted-foreground hidden lg:table-cell text-xs">{log.sent_at ? new Date(log.sent_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
